import React, { useEffect, useState } from "react";
import { Image as ImageIcon, Upload, ExternalLink, Plus, X } from "lucide-react";
import { api, TIMEOUTS } from "@/lib/api";
import { humanBytes, policy } from "@/lib/policy";
import { useFileUrl } from "@/lib/fileAccess";
import { toast } from "sonner";
import Avatar from "@/components/Avatar";
import * as avatarStore from "@/lib/avatarStore";

const DICEBEAR_STYLES = ["notionists", "lorelei", "adventurer", "micah", "personas", "thumbs", "glass", "shapes"];
const DICEBEAR_BASE = "https://api.dicebear.com/7.x";

// A hook per preset, so each one asks for its own token — they are batched into
// a single request by lib/fileAccess.
function PresetImage({ fileId }) {
  const url = useFileUrl(fileId);
  if (!url) return <div className="w-full aspect-square bg-surface-2" />;
  return <img src={url} alt="avatar presetat" className="w-full aspect-square object-cover" />;
}

export default function AvatarSettings({ currentUser }) {
  const id = currentUser.id;
  const [current, setCurrent] = useState({
    avatar_type: currentUser.avatar_type || "dicebear",
    avatar_url: currentUser.avatar_url || null,
    dicebear_style: currentUser.dicebear_style || "notionists",
  });
  const [tab, setTab] = useState("presets");
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [grav, setGrav] = useState(null);  // null = loading; {exists, url}
  const [presets, setPresets] = useState([]);
  const [canManage, setCanManage] = useState(false);
  // Limita vine de la server. Scrisă aici, ar fi a treia copie a aceleiași
  // cifre — și a treia care poate rămâne în urmă.
  const [maxAvatar, setMaxAvatar] = useState(0);

  useEffect(() => {
    policy().then((p) => setMaxAvatar(p.AVATAR_MAX_MB || 0)).catch(() => {});
  }, []);

  const fetchPresets = () => api.get("/avatar-presets")
    .then((r) => { setPresets(r.data.presets || []); setCanManage(!!r.data.can_manage); })
    .catch(() => {});

  useEffect(() => { if (tab === "presets") fetchPresets(); }, [tab]);

  const uploadPreset = async (f) => {
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) { toast.error("Doar JPEG, PNG sau WebP"); return; }
    if (maxAvatar && f.size > maxAvatar) {
      toast.error(`«${f.name}» are ${humanBytes(f.size)}, maximul este ${humanBytes(maxAvatar)}.`);
      return;
    }
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", f); await api.post("/avatar-presets", fd, { timeout: TIMEOUTS.upload }); await fetchPresets(); toast.success("Presetare adăugată"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Încărcarea nu a reușit"); }
    finally { setBusy(false); }
  };

  const deletePreset = async (pid) => {
    setBusy(true);
    try { await api.delete(`/avatar-presets/${pid}`); await fetchPresets(); }
    catch (e) { toast.error("Eliminarea nu a reușit"); }
    finally { setBusy(false); }
  };

  // Apply a config change everywhere instantly (prime the shared store).
  const applyCfg = (cfg) => {
    const next = { avatar_type: cfg.avatar_type, avatar_url: cfg.avatar_url, dicebear_style: cfg.dicebear_style || current.dicebear_style };
    setCurrent(next);
    avatarStore.prime(id, next);
  };

  const pickPreset = async (src) => {
    setBusy(true);
    try {
      const { data } = await api.patch("/users/me/avatar", { avatar_type: "preset", avatar_url: src });
      applyCfg(data);
      toast.success("Avatar actualizat");
    } catch (e) { toast.error(e?.response?.data?.detail || "Actualizarea nu a reușit"); }
    finally { setBusy(false); }
  };

  const pickStyle = async (style) => {
    setBusy(true);
    try {
      const { data } = await api.patch("/users/me/avatar", { avatar_type: "dicebear", dicebear_style: style });
      applyCfg(data);
      toast.success("Avatar actualizat");
    } catch (e) { toast.error(e?.response?.data?.detail || "Actualizarea nu a reușit"); }
    finally { setBusy(false); }
  };

  const onFile = (f) => {
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) { toast.error("Doar JPEG, PNG sau WebP"); return; }
    if (maxAvatar && f.size > maxAvatar) {
      toast.error(`«${f.name}» are ${humanBytes(f.size)}, maximul este ${humanBytes(maxAvatar)}.`);
      return;
    }
    setFile(f);
    setFilePreview(URL.createObjectURL(f));
  };

  const saveUpload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/users/me/avatar/upload", fd,
        { timeout: TIMEOUTS.avatarUpload });
      applyCfg({ avatar_type: "upload", avatar_url: data.avatar_url });
      setFile(null); setFilePreview(null);
      toast.success("Poză încărcată");
    } catch (e) { toast.error(e?.response?.data?.detail || "Încărcarea nu a reușit"); }
    finally { setBusy(false); }
  };

  const removeUpload = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/users/me/avatar", { avatar_type: "dicebear", dicebear_style: current.dicebear_style });
      applyCfg(data);
      toast.success("Revenit la avatarul generat");
    } catch (e) { toast.error(e?.response?.data?.detail || "Nu a reușit"); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (tab !== "gravatar") return;
    setGrav(null);
    api.get("/users/me/avatar/gravatar-check")
      .then((r) => setGrav(r.data))
      .catch(() => setGrav({ exists: false, url: "" }));
  }, [tab]);

  const useGravatar = async () => {
    if (!grav?.url) return;
    setBusy(true);
    try {
      const { data } = await api.patch("/users/me/avatar", { avatar_type: "gravatar", avatar_url: grav.url });
      applyCfg(data);
      toast.success("Se folosește Gravatar");
    } catch (e) { toast.error(e?.response?.data?.detail || "Nu a reușit"); }
    finally { setBusy(false); }
  };

  const TABS = [["presets", "Presetări"], ["generated", "Generat"], ["upload", "Încarcă o poză"], ["gravatar", "Gravatar"]];

  return (
    <section className="bg-surface border border-line/10" data-testid="avatar-settings">
      <div className="px-5 py-4 border-b border-line/10 flex items-center gap-2">
        <ImageIcon className="w-4 h-4 text-brand" />
        <h3 className="font-bold tracking-tight">Avatar</h3>
      </div>
      <div className="p-5">
        <div className="flex items-center gap-4 mb-5">
          <span className="rounded-full overflow-hidden w-24 h-24 border border-line/10 inline-flex">
            <Avatar user={currentUser} size={96} className="rounded-full" />
          </span>
          <div className="text-xs text-fg/50">
            <p className="font-medium text-fg text-sm">{currentUser.name}</p>
            <p className="mono">{currentUser.email}</p>
          </div>
        </div>

        <div className="flex gap-1 mb-4 border-b border-line/10">
          {TABS.map(([k, label]) => (
            <button
              key={k} onClick={() => setTab(k)} data-testid={`avatar-tab-${k}`}
              className={`px-3 py-1.5 text-xs border-b-2 -mb-px transition-colors ${
                tab === k ? "border-brand text-brand" : "border-transparent text-fg/60 hover:text-fg"
              }`}
            >{label}</button>
          ))}
        </div>

        {tab === "presets" && (
          <div className="space-y-3">
            {presets.length === 0 ? (
              <p className="text-xs text-fg/40 py-4 text-center">
                {canManage ? "Nicio presetare încă — adaugă mai jos." : "Nu există încă avataruri presetate."}
              </p>
            ) : (
              <div className="grid grid-cols-5 gap-3" data-testid="preset-grid">
                {presets.map((p) => {
                  const active = current.avatar_type === "preset" && current.avatar_url === p.id;
                  return (
                    <div key={p.id} className="relative">
                      <button
                        disabled={busy} onClick={() => pickPreset(p.id)} data-testid={`preset-${p.id}`}
                        className={`block w-full p-1 border bg-surface-2 transition-colors ${
                          active ? "border-brand" : "border-line/10 hover:border-line/30"
                        }`}
                        style={active ? { boxShadow: "0 0 0 1px rgb(var(--accent))" } : undefined}
                      >
                        <PresetImage fileId={p.id} />
                      </button>
                      {canManage && (
                        <button
                          onClick={() => deletePreset(p.id)} title="Elimină presetarea" data-testid={`preset-del-${p.id}`}
                          className="absolute -top-1.5 -right-1.5 bg-surface border border-line/20 text-fg/60 hover:text-red-400 rounded-full p-0.5"
                        ><X className="w-3 h-3" /></button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {canManage && (
              <label data-testid="preset-upload" className="inline-flex items-center gap-1.5 text-xs text-brand hover:text-brand-2 cursor-pointer">
                <Plus className="w-3.5 h-3.5" /> {busy ? "Se încarcă…" : "Adaugă un avatar presetat"}
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => uploadPreset(e.target.files?.[0])} />
              </label>
            )}
          </div>
        )}

        {tab === "generated" && (
          <div className="grid grid-cols-4 gap-3" data-testid="dicebear-grid">
            {DICEBEAR_STYLES.map((style) => {
              const active = current.avatar_type === "dicebear" && current.dicebear_style === style;
              return (
                <button
                  key={style} disabled={busy} onClick={() => pickStyle(style)} data-testid={`dicebear-${style}`}
                  className={`p-2 border bg-surface-2 flex flex-col items-center gap-1 transition-colors ${
                    active ? "border-brand" : "border-line/10 hover:border-line/30"
                  }`}
                  style={active ? { boxShadow: "0 0 0 1px rgb(var(--accent))" } : undefined}
                >
                  <img src={`${DICEBEAR_BASE}/${style}/svg?seed=${encodeURIComponent(id)}`} alt={style} width={48} height={48} className="bg-surface" />
                  <span className="text-[10px] text-fg/60 truncate w-full text-center">{style}</span>
                </button>
              );
            })}
          </div>
        )}

        {tab === "upload" && (
          <div className="space-y-3">
            <label data-testid="avatar-file" className="block border border-dashed border-line/20 hover:border-line/40 p-6 text-center cursor-pointer transition-colors">
              <Upload className="w-5 h-5 mx-auto mb-2 text-fg/40" />
              <span className="text-xs text-fg/60">{file ? file.name
                 : `Alege o imagine — JPEG / PNG / WebP${maxAvatar ? `, maximum ${humanBytes(maxAvatar)}` : ""}`}</span>
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
            </label>
            {filePreview && (
              <div className="flex items-center gap-3">
                <img src={filePreview} alt="previzualizare" className="w-20 h-20 rounded-full object-cover border border-line/10" />
                <button onClick={saveUpload} disabled={busy} className="btn-primary text-sm px-3 py-1.5 disabled:opacity-50">{busy ? "Se salvează…" : "Salvează"}</button>
                <button onClick={() => { setFile(null); setFilePreview(null); }} className="text-sm text-fg/50 hover:text-fg">Anulează</button>
              </div>
            )}
            {current.avatar_type === "upload" && !filePreview && (
              <button data-testid="avatar-remove" onClick={removeUpload} disabled={busy} className="text-xs text-red-400 hover:text-red-300 disabled:opacity-50">
                Elimină poza (revino la cea generată)
              </button>
            )}
          </div>
        )}

        {tab === "gravatar" && (
          <div className="space-y-3">
            {grav === null ? (
              <p className="text-xs text-fg/40">Se caută Gravatar…</p>
            ) : grav.exists ? (
              <div className="flex items-center gap-3">
                <img src={`${grav.url}?s=80`} alt="gravatar" className="w-20 h-20 rounded-full object-cover border border-line/10" />
                <button data-testid="use-gravatar" onClick={useGravatar} disabled={busy} className="btn-primary text-sm px-3 py-1.5 disabled:opacity-50">Folosește Gravatar</button>
              </div>
            ) : (
              <p className="text-xs text-fg/60 leading-relaxed">
                Niciun Gravatar pentru <span className="mono text-fg">{currentUser.email}</span>.{" "}
                <a href="https://gravatar.com" target="_blank" rel="noreferrer" className="text-brand hover:text-brand-2 inline-flex items-center gap-0.5">
                  Creează-ți unul pe gravatar.com <ExternalLink className="w-3 h-3" />
                </a>
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
